import React, { useEffect, useMemo, useCallback, useState } from 'react'
import classnames from 'classnames/bind'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
  primitives,
} from '@adc/blueprint'
import * as graph from '@adc/sdk'
import {
  Text,
  CurrencyInputField,
  useApplicationModals,
  BidConfirmationContent,
  // @ts-ignore no types
} from '@adc/ui-adc'
import { formatMoneyWithCentsExceptZero } from 'common/utils/formatters'
import { isTPSOnline } from 'common/utils/listing'
import { ZERO_DOLLARS } from 'common/components/bidBanner/constants'
import CostBreakdown from 'common/components/bidBanner/costBreakdown'
import BidHistoryTrigger from 'common/modules/bidBanner/bidHistorySection/trigger'
import { BidHistorySection } from 'common/modules/bidBanner/bidHistorySection'
import BidHistory from 'common/modules/bidHistory'
import BidButton from '../bidButton'
import { BuyersPremiumAndTotal } from '../costBreakdown'
import SalesDeposit from '../salesDeposit'
import EarnestMoneyDeposit from '../earnestMoneyDeposit'
// @ts-ignore no types
import styles from './styles.scss'
const cx = classnames.bind(styles)

type AuctionUser = primitives.AuctionUser

interface BidFormUIProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  bidAmount: number
  handleBidButtonClick: (vars: any) => void
  hasError: boolean
  isPerformingBid: boolean
  onChange: () => void
  performBid: () => void
  property: any
  user: AuctionUser
  warning: boolean
  featureFlags: any
  isProxyBiddingActive: boolean
}
const BidFormUI = (props: BidFormUIProps) => {
  const {
    bidAmount,
    handleBidButtonClick: onClick,
    hasError,
    isPerformingBid,
    onChange,
    performBid,
    warning,
    featureFlags,
    isProxyBiddingActive,
    listing,
    segment,
    user,
  } = props

  const { open, update } = useApplicationModals()

  const isTPSOnlineListing = isTPSOnline(listing, segment)

  const bidInputError = `Bid amount must be at least ${formatMoneyWithCentsExceptZero(
    segment.__methods__.getNextBidAmount(),
    ZERO_DOLLARS
  )}.`
  const calculations = listing.__methods__.getCostCalculations(bidAmount)

  const { required, acknowledged } =
    segment.__methods__.calculateOccupancyDisclaimerRequirements(listing)

  const [occupancyAcknowledged, setOccupancyAcknowledged] =
    useState(acknowledged)

  const requiresOccupancyAcknowledgment = Boolean(
    featureFlags['fe.resi.bid_confirmation_occupancy_acknowledgement'] &&
      required
  )

  const isConfirmBidButtonDisabled = Boolean(
    isPerformingBid ||
      (requiresOccupancyAcknowledgment && !occupancyAcknowledged)
  )

  const bidConfirmationModalProps = useMemo(
    () => ({
      children: (
        <BidConfirmationContent
          type={graph.enums.OnlineAuctionSegmentBidType.Regular}
          listing={listing}
          segment={segment}
          user={user}
          bidAmount={bidAmount}
          isPerformingBid={isPerformingBid}
          isOccupancyDisclaimerRequired={requiresOccupancyAcknowledgment}
          occupancyDisclaimerAcknowledged={occupancyAcknowledged}
          setOccupancyAcknowledged={setOccupancyAcknowledged}
        />
      ),
      elmId: 'pdp_bid_confirmation_modal',
      size: 'tiny',
      title: 'Confirm Bid',
      confirmButton: {
        onClick: performBid,
        disabled: isConfirmBidButtonDisabled,
      },
    }),
    [
      user,
      listing,
      segment,
      occupancyAcknowledged,
      setOccupancyAcknowledged,
      isPerformingBid,
      bidAmount,
      isConfirmBidButtonDisabled,
      requiresOccupancyAcknowledgment,
      performBid,
    ]
  )

  useEffect(() => {
    update(bidConfirmationModalProps)
  }, [bidConfirmationModalProps, update])

  const openBidConfirmationModal = useCallback(() => {
    open('confirmation', bidConfirmationModalProps)
  }, [open, bidConfirmationModalProps])

  return (
    <>
      <div className={cx('u-mb-3')}>
        <div className={cx('label-value')}>
          <Text color='gray' content='Your Next Bid' />
          <CurrencyInputField
            elmId='place_bid'
            onChange={onChange}
            value={
              bidAmount
                ? formatMoneyWithCentsExceptZero(bidAmount, ZERO_DOLLARS)
                : ''
            }
            className={cx('bid-input', { warning })}
            hideWrapper
            allowDecimal={isTPSOnlineListing}
          />
        </div>
        {warning && (
          <Text
            className={cx('u-mt-1', 'u-text-right')}
            data-elm-id='place_bid_input_error'
            color='error'
            size='tiny'
            content={bidInputError}
          />
        )}
      </div>

      <BuyersPremiumAndTotal
        listing={listing}
        bidAmount={bidAmount}
        hasError={hasError}
      />

      <div className={cx('centered-section', 'u-mb-3')}>
        {!listing.event?.trustee_sale ? (
          <EarnestMoneyDeposit
            listing={listing}
            earnestMoneyDeposit={calculations.earnestMoneyDeposit}
            hasError={hasError}
          />
        ) : (
          <SalesDeposit
            salesDeposit={listing.trustee?.online_state_deposit_amount}
          />
        )}
      </div>

      <BidButton
        isProxyBiddingActive={isProxyBiddingActive}
        bidAmount={bidAmount}
        containerClassName={cx('u-mb-4')}
        hasError={hasError}
        onClick={() => onClick(openBidConfirmationModal)}
      />

      {segment && segment.__typename === 'OnlineAuctionSegment' ? (
        <div className={cx('modal-links-container')}>
          <BidHistory
            listing={listing}
            trigger={<BidHistoryTrigger />}
            featureFlags={featureFlags}
          />
          {/* TODO: bid history bidding status removal */}
          <CostBreakdown
            bidAmount={bidAmount}
            isBidAmountValid={!hasError}
            segment={segment}
            listing={listing}
          />
        </div>
      ) : (
        <BidHistorySection listing={listing} featureFlags={featureFlags} />
      )}
    </>
  )
}

export default BidFormUI
